// FieldVision Admin - Pilot List Component
import React, { useState, useEffect } from 'react';
import { getPilots, createPilot, updatePilot, deletePilot, Pilot } from '../services/api';

interface PilotFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  droneType: string;
  isActive: boolean;
}

const INITIAL_FORM: PilotFormData = {
  name: '',
  email: '',
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  droneType: 'Mavic 3M',
  isActive: true,
};

const DRONE_TYPES = ['Mavic 3M', 'Mavic 3 Enterprise', 'Phantom 4 RTK', 'Matrice 300'];

const PilotList: React.FC = () => {
  const [pilots, setPilots] = useState<Pilot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPilot, setEditingPilot] = useState<Pilot | null>(null);
  const [formData, setFormData] = useState<PilotFormData>(INITIAL_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const loadPilots = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPilots();
      setPilots(data.pilots);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPilots();
  }, []);

  const openCreateModal = () => {
    setEditingPilot(null);
    setFormData(INITIAL_FORM);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (pilot: Pilot) => {
    setEditingPilot(pilot);
    setFormData({
      name: pilot.name,
      email: pilot.email,
      phone: pilot.phone || '',
      address: pilot.address || '',
      postalCode: pilot.postalCode || '',
      city: pilot.city || '',
      droneType: pilot.droneType || 'Mavic 3M',
      isActive: pilot.isActive,
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPilot(null);
    setFormData(INITIAL_FORM);
    setFormError('');
  };

  const handleFormChange = (field: keyof PilotFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      if (editingPilot) {
        await updatePilot(editingPilot.pilotId, formData);
        setSuccess(`Pilot "${formData.name}" opdateret`);
      } else {
        await createPilot(formData);
        setSuccess(`Pilot "${formData.name}" oprettet`);
      }
      closeModal();
      loadPilots();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (pilot: Pilot) => {
    if (!window.confirm(`Er du sikker på at du vil deaktivere "${pilot.name}"?`)) return;

    try {
      await deletePilot(pilot.pilotId);
      setSuccess(`Pilot "${pilot.name}" deaktiveret`);
      loadPilots();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>Piloter</h2>
        <button onClick={openCreateModal} style={styles.createButton}>
          + Ny pilot
        </button>
      </div>

      {/* Alerts */}
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Loading */}
      {loading && <div style={styles.loading}>Henter piloter...</div>}

      {/* Empty state */}
      {!loading && pilots.length === 0 && (
        <div style={styles.empty}>Ingen piloter fundet</div>
      )}

      {/* Pilot Table */}
      {!loading && pilots.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Navn</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Telefon</th>
              <th style={styles.th}>By</th>
              <th style={styles.th}>Drone</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {pilots.map((pilot) => (
              <tr key={pilot.pilotId} style={styles.tr}>
                <td style={styles.td}>
                  <span style={styles.pilotName}>{pilot.name}</span>
                </td>
                <td style={styles.td}>{pilot.email}</td>
                <td style={styles.td}>{pilot.phone || '-'}</td>
                <td style={styles.td}>{pilot.city || '-'}</td>
                <td style={styles.td}>
                  <span style={styles.droneType}>{pilot.droneType}</span>
                </td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: pilot.isActive ? '#dcfce7' : '#f3f4f6',
                      color: pilot.isActive ? '#16a34a' : '#6b7280',
                    }}
                  >
                    {pilot.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={styles.actions}>
                    <button
                      onClick={() => openEditModal(pilot)}
                      style={styles.editButton}
                    >
                      ✏️
                    </button>
                    {pilot.isActive && (
                      <button
                        onClick={() => handleDelete(pilot)}
                        style={styles.deleteButton}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              {editingPilot ? 'Rediger pilot' : 'Ny pilot'}
            </h3>

            {formError && <div style={styles.formError}>{formError}</div>}

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>Navn *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Telefon</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleFormChange('phone', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Adresse</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleFormChange('address', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Postnr</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => handleFormChange('postalCode', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>By</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleFormChange('city', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Drone type</label>
                  <select
                    value={formData.droneType}
                    onChange={(e) => handleFormChange('droneType', e.target.value)}
                    style={styles.input}
                  >
                    {DRONE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                {editingPilot && (
                  <div style={styles.field}>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => handleFormChange('isActive', e.target.checked)}
                        style={styles.checkbox}
                      />
                      Aktiv
                    </label>
                  </div>
                )}
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={styles.cancelButton}
                  disabled={formLoading}
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  style={styles.submitButton}
                  disabled={formLoading}
                >
                  {formLoading ? 'Gemmer...' : editingPilot ? 'Gem ændringer' : 'Opret pilot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
  },
  createButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'white',
    backgroundColor: '#16a34a',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  error: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  success: {
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    color: '#16a34a',
    marginBottom: '16px',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#9ca3af',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  tr: {
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '14px',
    color: '#1f2937',
  },
  pilotName: {
    fontWeight: 500,
  },
  droneType: {
    fontSize: '12px',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '6px 10px',
    fontSize: '14px',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '6px 10px',
    fontSize: '14px',
    backgroundColor: '#fef2f2',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalTitle: {
    margin: '0 0 20px 0',
    fontSize: '20px',
    fontWeight: 600,
  },
  formError: {
    padding: '12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    outline: 'none',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '8px',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    color: '#6b7280',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'white',
    backgroundColor: '#16a34a',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};

export default PilotList;
